import { Questionnaire } from '../../types/fhir';
import { getNachsorgeMaterialDocument } from '../TherapyMaterial/therapyMaterial';
import { TaskSummary } from './types';

export type AftercareTaskMetadata = {
    title?: string;
    week?: number | string;
};

export type AftercareTaskMetadataCache = Map<string, AftercareTaskMetadata>;

const getQuestionnaireTitle = (questionnaire: Questionnaire): string | undefined =>
    questionnaire.title?.trim() || questionnaire.name?.trim() || undefined;

const getWeekFromTitle = (title: string | undefined): number | undefined => {
    const match = title?.match(/\bWoche\s+(\d+)\b/i);
    if (!match) {
        return undefined;
    }

    const week = Number(match[1]);
    return Number.isSafeInteger(week) ? week : undefined;
};

const getAftercareTaskMetadata = (questionnaire: Questionnaire): AftercareTaskMetadata => {
    const title = getQuestionnaireTitle(questionnaire);

    try {
        const document = getNachsorgeMaterialDocument(questionnaire);
        return {
            title: document.title,
            week: document.week,
        };
    } catch {
        // The item payload is authoritative. Seeded legacy titles provide a safe fallback
        // when that payload is malformed, and must not hide the assigned task itself.
        return { title, week: getWeekFromTitle(title) };
    }
};

export const enrichAftercareTasks = async (
    tasks: TaskSummary[],
    loadQuestionnaire: (questionnaireId: string) => Promise<Questionnaire>,
    cache: AftercareTaskMetadataCache,
    onError?: (questionnaireId: string, error: unknown) => void,
): Promise<TaskSummary[]> => {
    const questionnaireIds = Array.from(
        new Set(tasks.map((task) => task.questionnaireId).filter((id): id is string => Boolean(id))),
    );

    await Promise.all(
        questionnaireIds.map(async (questionnaireId) => {
            if (cache.has(questionnaireId)) {
                return;
            }

            try {
                const questionnaire = await loadQuestionnaire(questionnaireId);
                cache.set(questionnaireId, getAftercareTaskMetadata(questionnaire));
            } catch (error) {
                onError?.(questionnaireId, error);
            }
        }),
    );

    return tasks.map((task) => {
        const metadata = task.questionnaireId ? cache.get(task.questionnaireId) : undefined;
        if (!metadata) {
            return task;
        }

        return {
            ...task,
            questionnaireName: metadata.title || task.questionnaireName,
            week: metadata.week,
        };
    });
};
